import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { bodyAreas, quoteInitialValues as initialValues, quoteSchema as validationSchema } from '.';
import { FormikControl } from '..';
import {
    NETLIFY_FUNCTIONS_PATH,
    handleSendEmail,
    GetEmailTemplate,
    getFacebookCookies,
    getOptimizelyData,
    validateWithPossibleNow,
    syncPardotTracking,
} from '../../../utils';
import { navigate } from 'gatsby';
import axios from 'axios';
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    AttributionSurvey,
    ConsentMsg,
    SmsOptInCheckbox,
    SMS_CONSENT_LEGAL_TERMS,
} from '../shared';
import { useScrollToAnchor } from '../../../hooks/useScrollToAnchor.ts';

export function QuoteForm({ siteData }) {
    initialValues.location = siteData.currentMetro.stores[0].salesforceValue;

    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    const [formValues, setFormValues] = useState(null);
    const [smsOptIn, setSmsOptIn] = useState(false);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('quote-form-container');
        helpers.setSubmitting(true);
        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();
        try {
            // Validate phone and email with PossibleNow
            const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                values.phone,
                values.email
            );

            const formData = {
                ...values,
                updates: true,
                // Checked => the user opted IN to marketing SMS, so the opt-OUT flag is false.
                marketing_sms_optout: !smsOptIn,
                description: `Instant Estimate: ${values.bodyAreas.join(' | ')} | ${values.description}`,
                cloudamp__data__c: localStorage.getItem('cloudamp__data__c'),
                formType: 'quote',
                fbp,
                fbc,
                experiment_exposures,
                experiment_user_ID,
                phoneFound,
                wireless,
                emailFound,
                result,
                flags,
            };

            // Store form values for use in attribution survey
            setFormValues(formData);

            await axios.post(`${NETLIFY_FUNCTIONS_PATH}form-handler`, formData);
            syncPardotTracking(formData);

            // Show attribution survey instead of immediate success
            setShowAttributionSurvey(true);

            helpers.setSubmitting(false);
            helpers.setStatus('Success');

            const htmlTemplateForCustomQuote = GetEmailTemplate.customQuoteEmailTemplate(
                values.first_name,
                siteData.currentMetro.name
            );
            handleSendEmail(values.email, 'Milan Laser Custom Quote', htmlTemplateForCustomQuote, 'Custom Quote');
            navigate('?quote-form-success'); // This is for Marketing
            helpers.resetForm();
            // smsOptIn lives outside Formik; reset it too so the express-consent box never carries over.
            setSmsOptIn(false);
        } catch (error) {
            console.error('Form submission error:', error);
            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setFormValues(null);
            setShowAttributionSurvey(false);
        }
    };

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false);
        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    };

    return (
        <div id="quote-form-container" className="quote-form-container" style={{ minHeight: '300px' }}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
            >
                {(formik) => {
                    if (formik.isSubmitting) {
                        return <FormSubmitting />;
                    }

                    // Handle error state
                    if (formik.status === 'Failed') {
                        return <FormFailed />;
                    }

                    {
                        /* At this point the form is submitted */
                    }
                    if (formValues) {
                        if (showAttributionSurvey) {
                            return (
                                <AttributionSurvey
                                    formValues={formValues}
                                    onComplete={handleAttributionComplete}
                                    isFullWidth={false}
                                />
                            );
                        }
                        return <FormSuccess />;
                    }

                    return (
                        <Form id="quote-form" className="quote-form w-100 pb-4">
                            {!showAttributionSurvey && !formValues && !formik.isSubmitting && (
                                <h2 className="milan-text-primary h3 fw-normal text-center">
                                    Fill out the form below to request your quote.
                                </h2>
                            )}
                            <div className="form-inputs-wrapper pt-4">
                                <div className="row gy-3 justify-content-center mx-auto">
                                    <div className="col-md-5">
                                        <FormikControl
                                            control="input"
                                            name="first_name"
                                            placeholder="First Name"
                                            type="text"
                                            role="input"
                                            aria-required
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <FormikControl
                                            control="input"
                                            name="last_name"
                                            placeholder="Last Name"
                                            type="text"
                                            role="input"
                                            aria-required
                                        />
                                    </div>
                                </div>
                                <div className="row gy-3 justify-content-center mx-auto pt-3">
                                    <div className="col-md-5">
                                        <FormikControl
                                            control="phoneInput"
                                            name="phone"
                                            placeholder="Phone Number"
                                            type="tel"
                                            role="input"
                                            aria-required
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <FormikControl
                                            control="emailInput"
                                            name="email"
                                            placeholder="Your Email"
                                            role="input"
                                            aria-required
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <FormikControl
                                            control="selectLocation"
                                            name="location"
                                            label="Location"
                                            milanStores={siteData.milanStores}
                                        />
                                    </div>
                                </div>
                                <h6 className="text-center mt-3 mt-sm-4 mt-lg-5 milan-text-primary">
                                    Please select up to 3 areas you’re interested in.
                                </h6>
                                <div className="row justify-content-center mt-4 pt-1 checkboxes">
                                    <ul className="col-sm-10 row">
                                        <FormikControl
                                            control="areasCheckboxes"
                                            name="bodyAreas"
                                            options={bodyAreas}
                                            formik={formik}
                                        />
                                    </ul>
                                </div>
                                <div className="row justify-content-center mt-4">
                                    <div className="col-md-10 row">
                                        <h6 className="text-center milan-text-primary">
                                            Other Areas{' '}
                                            <span className="d-none d-md-inline">
                                                (we treat almost any area of the body)
                                            </span>
                                            :
                                        </h6>
                                        <FormikControl
                                            control="textarea"
                                            name="description"
                                            placeholder="Describe any other areas you're interested in."
                                            rows="1"
                                        />
                                    </div>
                                </div>

                                <div className="row justify-content-center my-3">
                                    <div className="col-md-10 lh-1">
                                        <SmsOptInCheckbox
                                            checked={smsOptIn}
                                            onChange={(e) => setSmsOptIn(e.target.checked)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div id="actions-btns" className="row justify-content-center my-3 col-lg-10 m-auto">
                                <div className="col-sm-6 col-lg-4 text-center">
                                    <button
                                        className="milan-btn milan-red-btn w-100"
                                        type="submit"
                                        disabled={!formik.isValid}
                                    >
                                        {formik.isSubmitting ? (
                                            'Submitting...'
                                        ) : (
                                            <>
                                                Request a Quote <sup>*</sup>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="row justify-content-center my-3">
                                <div className="col-md-10 lh-1">
                                    {/* separator '. ' closes the Terms link before SMS_CONSENT_LEGAL_TERMS opens a new sentence */}
                                    <ConsentMsg
                                        cta="Request A Quote<sup>*</sup>"
                                        legalTerms={SMS_CONSENT_LEGAL_TERMS}
                                        separator=". "
                                        termsLabel="Terms"
                                    />
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
}

export default QuoteForm;
